import { _ } from "../../../polyglot/polyglot.js";
import { makeDropdown, titleRenderer } from "../../makeRoadmapStepContent.js";
import AbstractPayablesAndReceivablesStep from "./AbstractPayablesAndReceivablesStep.js";
import regionWidget from "../../../widgets/regionWidget.js";
import TextRepresentationAccountSpecificationVisitor from "../../../../models/accountSpecification/visitors/TextRepresentationAccountSpecificationVisitor.js";

/**
 * This allows the user to choose accounts for account paybable and
 * receivable.
 */
export default class ForecastPayablesAndReceivablesStep extends AbstractPayablesAndReceivablesStep {
	getTitle() {
		return _("Forecast payables and receivables");
	}

	_getSubtitle() {
		return _(
			"Forecasted income and costs will be held in accounts receivables and payables before they are paid, please set which accounts to be used.",
		);
	}

	_doSubmit({ onDone }) {
		const payableAccount =
			this._context.configuration.getForecastAccountsPayable();

		const receivableAccount =
			this._context.configuration.getForecastAccountsReceivable();

		if (!receivableAccount) {
			this.addSubmitError(_("A receivable account must be selected"));
		}

		if (!payableAccount) {
			this.addSubmitError(_("A payable account must be selected"));
		}

		if (this._missesDelays(this._getPayableDelays()))
			this._raiseBlankErrorForContent(this._payableDelays);

		if (this._missesDelays(this._getReceivableDelays()))
			this._raiseBlankErrorForContent(this._receivableDelays);

		onDone();
	}

	_getReceivableDelays() {
		return this._context.configuration.getForecastAccountsReceivableDelays();
	}

	_setReceivableDelays(value) {
		this._context.configuration.setForecastAccountsReceivableDelays(value);
	}

	_getPayableDelays() {
		return this._context.configuration.getForecastAccountsPayableDelays();
	}

	_setPayableDelays(value) {
		this._context.configuration.setForecastAccountsPayableDelays(value);
	}

	_getStepContent() {
		return (html) => {
			html.div(
				{ class: "accounts-receivable" },
				titleRenderer(_("Accounts receivable")),
				html.div(
					{ class: "actions" },
					this._getForecastAccountsReceivableDropdown(),
					this._accountReceivableDelaysRenderer(),
				),
			);

			html.div(
				{ class: "accounts-payable" },
				titleRenderer(_("Accounts payable")),
				html.div(
					{ class: "actions" },
					this._getForecastAccountsPayableDropdown(),
					this._accountPayableDelaysRenderer(),
				),
			);
		};
	}

	_getForecastAccountsReceivableDropdown() {
		const receivableAccount = this._getAccountsTextualRepresentation(
			this._context.configuration.getForecastAccountsReceivable(),
		);

		return makeDropdown({
			noDefaultSelection: true,
			noSelectionText: "",
			helpText: _(
				"Choose which asset account you want your unpaid forecasted revenues recorded to.",
			),
			dataTestName: "accounts-receivable",
			items: this._context.getAccountDropdownItems({
				selectedFn: (account) =>
					receivableAccount === account.getNumber(),
			}),
			onChange: (selection) =>
				this._context.configuration.setForecastAccountsReceivable(
					selection?.id,
				),
		});
	}

	_accountReceivableDelaysRenderer() {
		this._receivableDelays ??= regionWidget({
			content: this._formatDelays(this._getReceivableDelays()),
		});

		return this._actionRenderer({
			label: _("Delay settings"),
			value: this._receivableDelays,
			dataTestName: "accounts-receivable-delays",
			linkLabel: _("+ select delays"),
			helpText: _(
				"Choose which percentage of actual customer invoices you expect to be paid in each following month. It must add up to 100%",
			),
			click: () => this._getReceivableDelayDialog().show(),
		});
	}

	_getForecastAccountsPayableDropdown() {
		const payableAccount = this._getAccountsTextualRepresentation(
			this._context.configuration.getForecastAccountsPayable(),
		);

		return makeDropdown({
			noDefaultSelection: true,
			noSelectionText: "",
			helpText: _(
				"Choose which liability account you want your unpaid forecasted costs recorded to.",
			),
			dataTestName: "accounts-payable",
			items: this._context.getAccountDropdownItems({
				selectedFn: (account) => payableAccount === account.getNumber(),
			}),
			onChange: (selection) =>
				this._context.configuration.setForecastAccountsPayable(
					selection?.id,
				),
		});
	}

	_getAccountsTextualRepresentation(accounts) {
		return new TextRepresentationAccountSpecificationVisitor().visit(
			accounts,
		);
	}

	_accountPayableDelaysRenderer() {
		this._payableDelays ??= regionWidget({
			content: this._formatDelays(this._getPayableDelays()),
		});

		return this._actionRenderer({
			label: _("Delay settings"),
			value: this._payableDelays,
			dataTestName: "accounts-payable-delays",
			linkLabel: _("+ select delays"),
			helpText: _(
				"Choose which percentage of actual supplier invoices you expect to pay in each following month. It must add up to 100%",
			),
			click: () => this._getPayableDelayDialog().show(),
		});
	}
}
